import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Box,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { getUserContactInfo, loginFromKeyClock, checkMfaStatus } from "../Utils/loginServices";
import { version } from '../../package.json';

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import CommonLogo from "../Components/CommonLogo";
import forge from "node-forge";
import { getBrowserInfo, getClientIp, getDeviceName, getDeviceType, getOperatingSystem } from "../Utils/loginHelpers";
import { usePublicKey } from "../context/PublicKeyProvider";
import { EVENT_TYPES, trackEvent } from "../Utils/analytics";
import { userInfo } from "../Utils/dsa";

const containerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#e2e8f0', // bg-slate-200
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const loginContainerStyle = {
  width: '384px', // w-96
  maxWidth: '90%', // max-w-[90%]
  margin: '0 auto', // mx-auto
  minHeight: '32rem', // Making it bigger - 512px instead of 448px
  height: 'auto', // Allow it to grow if needed
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  backgroundColor: 'white',
  borderRadius: '0.25rem', // rounded (4px) - matching unified-login-ui
  gap: '1.25rem', // gap-5 (20px) - matching unified-login-ui
  padding: '2rem', // px-8 (32px) - matching unified-login-ui
  paddingTop: '2.5rem', // Extra padding at top
  paddingBottom: '2.5rem', // Extra padding at bottom
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-2xl - matching unified-login-ui
};

const loginButtonStyle = {
  width: '96px',
  backgroundColor: '#1e3a8a',
  color: 'white',
  padding: '8px 16px',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  outline: 'none',
  fontSize: '14px',
  fontWeight: '500',
  '&:hover': {
    backgroundColor: '#1e40af',
  },
  '&:disabled': {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
};

const versionStyle = {
  position: 'absolute',
  bottom: '16px',
  fontSize: '14px',
  color: '#6b7280',
};

const CentralAdminLogin = () => {
  const history = useHistory();
  const publicKey = usePublicKey();

  const usernameRef = useRef();
  const passwordRef = useRef();

  const [inputErrors, setError] = useState({
    username: false,
    password: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null); // null, 'TOTP_INACTIVE', 'TOTP_NOT_CONFIGURED', 'TOTP_CONFIGURED'
  const [mfaError, setMfaError] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaUrl, setMfaUrl] = useState("");

  const handleLogin = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setLoading(true);
    setMfaError("");

    // Basic validation
    let errors = false;
    if (usernameRef.current.value === "") {
      errors = true;
      setError({ ...inputErrors, username: "Username cannot be Empty" });
    }
    if (passwordRef.current.value === "") {
      errors = true;
      setError({ ...inputErrors, password: "Password cannot be Empty" });
    }
    if (usernameRef.current.value.length < 4) {
      errors = true;
      setError({
        ...inputErrors,
        username: "Username should be atleast 4 characters",
      });
    }
    if (errors) {
      setLoading(false);
      return;
    }

    const employeeId = usernameRef.current.value.trim();

    // If MFA is already configured and OTP field is shown, validate OTP and proceed with login
    if (mfaStatus === 'TOTP_CONFIGURED') {
      if (!otp || otp.trim() === "" || otp.length !== 6) {
        setLoading(false);
        toast.error("Please enter a valid 6-digit OTP");
        return;
      }
      console.log("employeeId: ", employeeId);
      console.log("otp: ", otp);
      console.log("mfaStatus: ", mfaStatus);
      // OTP is provided, proceed with login
      await proceedWithLogin(employeeId, otp);
      return;
    }

    // Check MFA status first
    try {
      const response = await checkMfaStatus(employeeId);
      
      // Check if status code is 200
      if (response?.status === 200) {
        const status = response?.data?.data?.status;
        
        if (status === 'TOTP_INACTIVE') {
          // Proceed with login immediately
          setMfaStatus('TOTP_INACTIVE');
          setMfaUrl("");
          await proceedWithLogin(employeeId);
        } else if (status === 'TOTP_NOT_CONFIGURED') {
          // Show error and disable login button
          setMfaStatus('TOTP_NOT_CONFIGURED');
          const mfaUrlFromResponse = response?.data?.data?.mfa_url || "";
          setMfaUrl(mfaUrlFromResponse);
          setMfaError("show");
          setLoading(false);
        } else if (status === 'TOTP_CONFIGURED') {
          // Show OTP field and wait for user to fill it
          setMfaStatus('TOTP_CONFIGURED');
          setMfaUrl(""); // Clear mfa_url for other statuses
          setOtp(""); // Reset OTP when showing the field
          setLoading(false);
          // OTP field will be shown, auto-trigger login when 6 digits entered
        } else {
          setMfaStatus(null);
          setMfaUrl("");
          setLoading(false);
        }
      } else {
        setMfaStatus(null);
        setMfaUrl("");
        setLoading(false);
        toast.error("Error checking MFA status. Please try again.");
      }
    } catch (error) {
      console.error("Error checking MFA status:", error);
      setMfaStatus(null);
      setMfaUrl("");
      setLoading(false);
      toast.error("Error checking MFA status. Please try again.");
    }
  };

  const proceedWithLogin = async (employeeId, otpValue = null) => {
    try {
      trackEvent(EVENT_TYPES.LOGIN.ATTEMPT);
    } catch (error) {
      console.error("Error tracking login attempt:", error);
      // Continue with login even if analytics fails
    }

    // Check if public key is loaded
    if (!publicKey || publicKey.trim() === "") {
      setLoading(false);
      toast.error("Public key not loaded. Please refresh the page and try again.");
      console.error("Public key is not available");
      return;
    }

    let publicKeyObj;
    try {
      publicKeyObj = forge.pki.publicKeyFromPem(publicKey);
    } catch (error) {
      setLoading(false);
      toast.error("Error processing public key. Please refresh the page and try again.");
      console.error("Error parsing public key:", error);
      return;
    }
    const encryptedCurrentPassword = forge.util.encode64(
      publicKeyObj.encrypt(passwordRef.current.value)
    );

    const { browserType, browserVersion } = getBrowserInfo();
    const operatingSystem = getOperatingSystem();
    const deviceType = getDeviceType();
    const deviceName = getDeviceName();
    const ipAddress = await getClientIp();

    const obj = {
      employee_code: employeeId,
      password: encryptedCurrentPassword,
      user_agent: "EMPLOYEE",
      source_channel: "CENTRAL_ADMIN_UI",
      destination_channel: "CENTRAL_ADMIN_UI",
      device_info: {
        device_id: "UNKNOWN_DEVICE_ID",
        device_token: "UNKNOWN_DEVICE_TOKEN",
        latitude: "",
        longitude: "",
        device_name: deviceName,
        device_type: deviceType,
        operating_system: operatingSystem,
        browser_type: browserType,
        browser_version: browserVersion,
        ip_address: ipAddress,
      }
    };

    // Add OTP to payload if MFA is configured
    // Use otpValue parameter if provided, otherwise use state
    const otpToUse = otpValue !== null ? otpValue : otp;
    if (mfaStatus === 'TOTP_CONFIGURED' && otpToUse) {
      obj.otp = otpToUse;
    }

    console.log("Calling loginFromKeyClock API with:", { employee_code: obj.employee_code, has_otp: !!obj.otp });
    loginFromKeyClock(obj)
      .then((res) => {
        getUserContactInfo()
          .then((info) => {
            setLoading(false);
            console.log("info: ", info);
            
            // Check if user has admin role (same as unified-login-ui LoginRedirect.js)
            const isAdminRole = (role) => {
              return ["ITADMIN", "OE", "AUDITOR", "OM", "ZAM", "HIA", "IAI", "FOE"].includes(role);
            };
            
            const userRole = info?.data?.role;
            
            // Validate role before proceeding
            if (!isAdminRole(userRole)) {
              toast.error(
                `Your role is not authorized to access this application.`
              );
              setLoading(false);
              return;
            }
            
            // Store user data in localStorage similar to Landing component
            const userData = {
              adminEmployeeId: info?.data?.employee_code,
              adminId: info?.data?.employee_code,
              employeeName: info?.data?.name || "ABC",
            };
            localStorage.setItem("userData", JSON.stringify(userData));
            
            // Call userInfo API to fetch and store user info (similar to Landing component)
            console.log("Calling userInfo API for employee:", userData.adminEmployeeId);
            userInfo(userData.adminEmployeeId)
              .then(() => {
                try {
                  trackEvent(EVENT_TYPES.LOGIN.SUCCESS, { 
                    redirect: "Central Admin", 
                    employee_id: info?.data?.employee_code
                  });
                } catch (error) {
                  console.error("Error tracking login success:", error);
                }
                
                // Use window.location.href to force a full page reload
                // This ensures App.js re-mounts and fetches userInfo properly, making all menu items visible
                console.log("Redirecting to dashboard with page reload");
                window.location.href = "/#/dashboard";
              })
              .catch((userInfoErr) => {
                console.error("Error fetching user info:", userInfoErr);
                // Still redirect even if userInfo fails
                try {
                  trackEvent(EVENT_TYPES.LOGIN.SUCCESS, { 
                    redirect: "Central Admin", 
                    employee_id: info?.data?.employee_code
                  });
                } catch (error) {
                  console.error("Error tracking login success:", error);
                }
                console.log("Redirecting to dashboard (after userInfo error)");
                window.location.href = "/#/dashboard";
              });
          })
          .catch((err) => {
            setLoading(false);
            console.error("Error in getUserContactInfo:", err);
            if (err?.response?.data?.errors[0]?.message) {
              try {
                trackEvent(EVENT_TYPES.LOGIN.FAILED, { 
                  reason: err?.response?.data?.errors?.[0]?.message, 
                  employee_Id: obj?.employee_code
                });
              } catch (error) {
                console.error("Error tracking login failed:", error);
              }
              toast.error(err?.response?.data?.errors[0]?.message);
            }
          });
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error in loginFromKeyClock:", err);
        if (err?.response?.data?.errors[0]?.message) {
          try {
            trackEvent(EVENT_TYPES.LOGIN.FAILED, { 
              reason: err?.response?.data?.errors?.[0]?.message, 
              employee_Id: obj?.employee_code 
            });
          } catch (error) {
            console.error("Error tracking login failed:", error);
          }
          toast.error(err?.response?.data?.errors[0]?.message);
        }
        if (err?.response?.data?.errors[0]?.code === "ERR_000037") {
          try {
            trackEvent(EVENT_TYPES.LOGIN.FAILED, { 
              reason: "ERR_000037", 
              employee_Id: obj?.employee_code 
            });
          } catch (error) {
            console.error("Error tracking login failed:", error);
          }
          history.push("/reset-password");
        }
      });
  };

  const InputHandler = () => {
    if (inputErrors.username) {
      setError({ ...inputErrors, username: false });
    }
    if (inputErrors.password) {
      setError({ ...inputErrors, password: false });
    }
    // Reset MFA status when employee ID or password changes
    if (mfaStatus !== null) {
      setMfaStatus(null);
      setMfaError("");
      setOtp("");
      setMfaUrl("");
    }
  };

  const handleOtpChange = (e) => {
    const otpValue = e.target.value;
    
    // Only allow numeric input and limit to 6 digits
    const numericOtp = otpValue.replace(/\D/g, '').slice(0, 6);
    setOtp(numericOtp);
  };

  return (
    <Box sx={containerStyle}>
      <Box 
        component="form" 
        onSubmit={handleLogin}
        sx={loginContainerStyle}
      >
        <CommonLogo />
        <Box sx={{ fontSize: "24px", fontWeight: 600, color: "#1e3a8a", marginTop: "0px", marginBottom: "4px" }}>
          Central Admin
        </Box>
        <TextField
          label="Employee ID"
          variant="standard"
          size="small"
          fullWidth
          inputRef={usernameRef}
          error={!!inputErrors.username}
          helperText={inputErrors.username}
          onInput={InputHandler}
          autoComplete="off"
          autoFocus
          sx={{
            '& .MuiInputLabel-root': {
              fontSize: '1rem',
              fontWeight: 400,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              fontSize: '1rem',
            },
            '& .MuiInputBase-input': {
              fontSize: '1rem', 
              paddingTop: '8px',
              paddingBottom: '8px',
            },
            marginBottom: '4px',
          }}
        />
        {mfaError && (
          <Box sx={{ width: '100%', color: '#ef4444', fontSize: '14px', marginTop: '-8px' }}>
            Please {" "}
            <a 
              href={mfaUrl || "#"} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: '#ef4444' }}
              onMouseEnter={(e) => e.target.style.color = '#b91c1c'}
              onMouseLeave={(e) => e.target.style.color = '#ef4444'}
            >
              click here
            </a>
            {" "}to complete MFA setup.
          </Box>
        )}
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="standard"
          size="small"
          fullWidth
          inputRef={passwordRef}
          error={!!inputErrors.password}
          helperText={inputErrors.password}
          onInput={InputHandler}
          autoComplete="off"
          sx={{
            '& .MuiInputLabel-root': {
              fontSize: '1rem', 
              fontWeight: 400,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              fontSize: '1rem',
            },
            '& .MuiInputBase-input': {
              fontSize: '1rem',
              paddingTop: '8px',
              paddingBottom: '8px',
            },
            marginBottom: '4px',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {mfaStatus === 'TOTP_CONFIGURED' && (
          <TextField
            label="OTP (Authenticator App)"
            variant="standard"
            size="small"
            fullWidth
            value={otp}
            autoComplete="off"
            placeholder="Enter 6-digit OTP from Authenticator App"
            onChange={handleOtpChange}
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '1rem', 
                fontWeight: 400,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                fontSize: '1rem',
              },
              '& .MuiInputBase-input': {
                fontSize: '1rem',
                paddingTop: '8px',
                paddingBottom: '8px',
              },
              marginBottom: '4px',
            }}
            inputProps={{
              maxLength: 6,
              pattern: "[0-9]*",
              inputMode: "numeric",
            }}
          />
        )}
        <button 
          type="submit" 
          disabled={loading || mfaStatus === 'TOTP_NOT_CONFIGURED' || !publicKey || publicKey.trim() === ""}
          style={loginButtonStyle}
        >
          {loading ? (
            <CircularProgress size="1rem" sx={{ color: "white" }} />
          ) : (
            "Login"
          )}
        </button>
      </Box>
      <Box sx={versionStyle}>
        Version: {version}
      </Box>
    </Box>
  );
};

export default CentralAdminLogin;

